import { NotificationBell, useMe, useSignOut } from "@brandex/dashboard";
import { Avatar, Button, Logo, ThemeToggle, cn } from "@brandex/ui";
import { Link as RouterLink, useRouterState } from "@tanstack/react-router";
import { LogOut, Menu, X } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";

import { useStaffAccess } from "./access";
import { visibleNav } from "./nav";

/** "super_admin" → "Super admin". */
export function roleLabel(role: string): string {
  const words = role.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The admin frame. Desktop: a full-height sidebar whose menu scrolls on its
 * own, so the account block and Sign out always stay in view. Phones: a top
 * bar with a menu button that opens the same menu as a drawer.
 */
export function AdminLayout({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  // While the drawer is open: Escape closes it and the page behind doesn't scroll.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const overflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.documentElement.style.overflow = overflow;
    };
  }, [open]);

  return (
    <div className="min-h-dvh md:flex md:gap-4 md:p-4">
      <header className="glass sticky top-[max(0.75rem,env(safe-area-inset-top,0px))] z-40 mx-3 mt-3 flex h-14 items-center gap-2 rounded-[var(--radius-xl)] px-2 md:hidden print:hidden">
        <Button size="icon" variant="ghost" aria-label="Open menu" aria-expanded={open} aria-controls="admin-drawer" onClick={() => setOpen(true)}>
          <Menu className="h-5 w-5" aria-hidden />
        </Button>
        <Brand />
        <NotificationBell className="ml-auto" />
      </header>

      {open ? (
        <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true" aria-label="Admin menu">
          <button type="button" tabIndex={-1} aria-hidden className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={close} />
          <aside id="admin-drawer" className="glass absolute bg-card inset-y-0 left-0 flex w-[min(18rem,85vw)] flex-col rounded-r-[var(--radius-xl)] pb-[env(safe-area-inset-bottom,0px)] pt-[env(safe-area-inset-top,0px)]">
            <div className="flex h-16 shrink-0 items-center gap-2 px-4">
              <Brand />
              <Button size="icon" variant="ghost" className="ml-auto" aria-label="Close menu" onClick={close} autoFocus>
                <X className="h-5 w-5" aria-hidden />
              </Button>
            </div>
            <NavList onNavigate={close} />
            <Account />
          </aside>
        </div>
      ) : null}

      <aside className="glass sticky top-4 hidden h-[calc(100dvh-2rem)] w-60 shrink-0 flex-col rounded-[var(--radius-xl)] md:flex print:hidden">
        <div className="flex h-16 shrink-0 items-center gap-2 px-5">
          <Brand />
          <NotificationBell className="ml-auto" />
        </div>
        <NavList />
        <Account />
      </aside>

      <main className="min-w-0 flex-1 px-4 py-4 md:px-6">{children}</main>
    </div>
  );
}

function Brand() {
  return (
    <span className="flex items-center gap-2">
      <Logo />
      <span className="rounded bg-coral-500/20 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-coral-400">Admin</span>
    </span>
  );
}

/** The pages this staff member can open; scrolls inside the sidebar when long. */
function NavList({ onNavigate }: { onNavigate?: () => void }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const access = useStaffAccess();
  return (
    <nav aria-label="Admin" className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pb-3">
      <ul className="grid gap-0.5">
        {visibleNav(access.data).map(({ to, label, icon: Icon }) => {
          const active = to === "/" ? pathname === "/" : pathname === to || pathname.startsWith(`${to}/`);
          return (
            <li key={to}>
              <RouterLink
                to={to}
                onClick={onNavigate}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "flex items-center gap-3 rounded-[var(--radius-md)] px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground",
                  active && "bg-azure-500/20 text-foreground shadow-[inset_0_0_0_1px_rgb(77_155_255/0.35)]",
                )}
              >
                <Icon className={cn("h-4 w-4 shrink-0", active && "text-azure-400")} aria-hidden />
                <span className="truncate">{label}</span>
              </RouterLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/** Who is signed in, the theme, and Sign out: pinned to the bottom. */
function Account() {
  const me = useMe();
  const signOut = useSignOut();
  return (
    <div className="shrink-0 border-t border-border p-3">
      {me.data !== undefined ? (
        <div className="flex items-center gap-3 px-1">
          <Avatar name={me.data.display_name} />
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{me.data.display_name}</p>
            <p className="truncate text-xs text-muted-foreground">{me.data.platform_roles.map(roleLabel).join(", ") || "Custom role"}</p>
          </div>
        </div>
      ) : null}
      <div className="mt-3 flex items-center justify-between gap-2">
        <ThemeToggle />
        <Button variant="ghost" size="sm" loading={signOut.isPending} onClick={() => signOut.mutate(false)}>
          <LogOut className="h-4 w-4" aria-hidden /> Sign out
        </Button>
      </div>
    </div>
  );
}
